
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as inner from '../inner'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Order} from '../core/Order';
import {FieldModelOrder} from '../core/Order';
import {AccountUpdateResponseParams} from './AccountUpdateResponseParams';
import {FieldModelAccountUpdateResponseParams} from './AccountUpdateResponseParams';

/**
 * MassResponseParams struct
 */
class MassResponseParams {
  /**
   * Initialize struct
   * @param {Order=} order
   * @param {AccountUpdateResponseParams=} updatedAccount
   * @constructor
   */
  constructor (order = null, updatedAccount = null) {
    this.Order = order
    this.UpdatedAccount = updatedAccount
  }

  /**
   * Copy struct (shallow copy)
   * @this {!MassResponseParams}
   * @param {!MassResponseParams} other Other struct
   * @returns {!MassResponseParams} This struct
   */
  copy (other) {
    if (other.Order != null) {
      this.Order = Order.fromObject(other.Order)
    } else {
      this.Order = null
    }
    if (other.UpdatedAccount != null) {
      this.UpdatedAccount = AccountUpdateResponseParams.fromObject(other.UpdatedAccount)
    } else {
      this.UpdatedAccount = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!MassResponseParams}
   * @returns {!MassResponseParams} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new MassResponseParamsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new MassResponseParamsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!MassResponseParams}
   * @param {!MassResponseParams} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof MassResponseParams)) {
      throw new TypeError('Instance of MassResponseParams is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!MassResponseParams}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Order: ((this.Order != null) ? this.Order : null),
      UpdatedAccount: ((this.UpdatedAccount != null) ? this.UpdatedAccount : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return MassResponseParams.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!MassResponseParams} other Object value
   * @returns {!MassResponseParams} Created struct
   */
  static fromObject (other) {
    return new MassResponseParams().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!MassResponseParams}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return MassResponseParams.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!MassResponseParams}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 4140
  }
}

export { MassResponseParams };

/**
 * Fast Binary Encoding MassResponseParams field model
 */
class FieldModelMassResponseParams extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Order = new fbe.FieldModelOptional(new FieldModelOrder(buffer, 4 + 4), buffer, 4 + 4)
    this.UpdatedAccount = new fbe.FieldModelOptional(new FieldModelAccountUpdateResponseParams(buffer, this.Order.fbeOffset + this.Order.fbeSize), buffer, this.Order.fbeOffset + this.Order.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelMassResponseParams}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelMassResponseParams}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Order.fbeSize + this.UpdatedAccount.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelMassResponseParams}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Order.fbeExtra + this.UpdatedAccount.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelMassResponseParams}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelMassResponseParams.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelMassResponseParams}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 4140
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelMassResponseParams}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelMassResponseParams}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Order.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Order.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Order.fbeSize

    if ((fbeCurrentSize + this.UpdatedAccount.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.UpdatedAccount.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UpdatedAccount.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelMassResponseParams}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelMassResponseParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelMassResponseParams}
   * @param {!MassResponseParams} fbeValue Default value, defaults is new MassResponseParams()
   * @returns {!MassResponseParams} MassResponseParams value
   */
  get (fbeValue = new MassResponseParams()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelMassResponseParams}
   * @param {!MassResponseParams} fbeValue MassResponseParams value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Order.fbeSize) <= fbeStructSize) {
      fbeValue.Order = this.Order.get()
    } else {
      fbeValue.Order = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Order.fbeSize

    if ((fbeCurrentSize + this.UpdatedAccount.fbeSize) <= fbeStructSize) {
      fbeValue.UpdatedAccount = this.UpdatedAccount.get()
    } else {
      fbeValue.UpdatedAccount = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UpdatedAccount.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelMassResponseParams}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelMassResponseParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelMassResponseParams}
   * @param {!MassResponseParams} fbeValue MassResponseParams value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelMassResponseParams}
   * @param {MassResponseParams} fbeValue MassResponseParams value
   */
  setFields (fbeValue) {
    this.Order.set(fbeValue.Order)
    this.UpdatedAccount.set(fbeValue.UpdatedAccount)
  }
}

export { FieldModelMassResponseParams };

/**
 * Fast Binary Encoding MassResponseParams model
 */
class MassResponseParamsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelMassResponseParams(this.buffer, 4)
  }

  /**
   * Get the MassResponseParams model
   * @this {!MassResponseParamsModel}
   * @returns {!FieldModelMassResponseParams} model MassResponseParams model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!MassResponseParamsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!MassResponseParamsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return MassResponseParamsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!MassResponseParamsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelMassResponseParams.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!MassResponseParamsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!MassResponseParamsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!MassResponseParamsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!MassResponseParamsModel}
   * @param {!MassResponseParams} value MassResponseParams value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!MassResponseParamsModel}
   * @param {!MassResponseParams} value MassResponseParams value, defaults is new MassResponseParams()
   * @return {!object} Deserialized MassResponseParams value and its size
   */
  deserialize (value = new MassResponseParams()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new MassResponseParams(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new MassResponseParams(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!MassResponseParamsModel}
   * @param {!number} prev Previous MassResponseParams model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { MassResponseParamsModel };
